import { useCallback, useEffect, useMemo, useState } from 'react'
import { produce } from 'immer'
import isError from 'lodash/isError'
import { Check } from 'lucide-react'
import { Card } from '@klear/ui/components/card'
import { toast } from '@klear/ui/hooks/use-toast'
import type { WizardStepGroupNavState } from '@klear/ui/wizard/nav-state'
import { bindWizardSteps, type WizardStepModule } from '@klear/ui/wizard/flow'
import { WizardFlowContent } from '@klear/ui/wizard/flow-content'
import { useReportedWizardFlow } from '@klear/ui/wizard/use-step-group-nav'
import {
  clearPurchaseOrderFormErrors,
  hasPurchaseOrderFormErrors,
  purchaseOrderFormFromStored,
  purchaseOrderFormToCreateWire,
  purchaseOrderFormToUpdateWire,
  validatePurchaseOrderForm,
  type PurchaseOrderForm,
  type PurchaseOrderFormDirty,
  type PurchaseOrderFormErrorMap
} from '@klear/purchase-order/wire.client'
import { PurchaseOrderFields } from '@/app/purchase-order/workflow/purchase-order-fields'
import { usePurchaseOrderMutations } from '@/app/purchase-order/workflow/use-purchase-order'

export type PurchaseOrderStepGroupResult = {
  purchaseOrderId: number
}

export type PurchaseOrderFlowMode = 'create' | 'edit'

const STEP = {
  DETAILS: 'purchase-order-details'
} as const

const ALL_DIRTY: PurchaseOrderFormDirty = {
  number: true,
  amount: true,
  effectiveDate: true,
  endDate: true,
  incoterm: true,
  notes: true
}

type PurchaseOrderStepCtx = {
  vals: PurchaseOrderForm
  errors: PurchaseOrderFormErrorMap
  handleFieldChange: <K extends keyof PurchaseOrderForm>(field: K, value: PurchaseOrderForm[K]) => void
}

const detailsStep: WizardStepModule<PurchaseOrderStepCtx> = {
  id: STEP.DETAILS,
  label: 'Details',
  isComplete: (ctx) => !hasPurchaseOrderFormErrors(validatePurchaseOrderForm(ctx.vals)),
  component: function PurchaseOrderDetailsStepContent({ ctx }) {
    return (
      <Card className="p-6 space-y-4">
        <h2 className="text-lg font-semibold text-foreground">Purchase order details</h2>
        <p className="text-sm text-muted-foreground">
          Number, value, dates, and terms for this purchase order. Downstream events (operational / invoice) hang off of
          it.
        </p>
        <PurchaseOrderFields values={ctx.vals} errors={ctx.errors} onFieldChange={ctx.handleFieldChange} />
      </Card>
    )
  }
}

const PURCHASE_ORDER_STEP_MODULES = [detailsStep]

export type PurchaseOrderFlowOptions = {
  traderId: number
  orderId: number
  initialData?: Record<string, unknown> | null
  existingId?: number | null
}

export type PurchaseOrderFlowProps = PurchaseOrderFlowOptions & {
  mode: PurchaseOrderFlowMode
  cancelOnFirstStep?: boolean
  onNavStateChange: (nav: WizardStepGroupNavState) => void
  onComplete: (result: PurchaseOrderStepGroupResult) => void
  onCancel: () => void
  onDirtyChange?: (dirty: boolean) => void
}

export function PurchaseOrderFlow({
  mode,
  traderId,
  orderId,
  initialData = null,
  existingId = null,
  cancelOnFirstStep = false,
  onNavStateChange,
  onComplete,
  onCancel,
  onDirtyChange
}: PurchaseOrderFlowProps) {
  const { createPurchaseOrder, updatePurchaseOrder, isSaving } = usePurchaseOrderMutations()
  const [values, setValues] = useState<PurchaseOrderForm>(() => purchaseOrderFormFromStored(initialData))
  const [errors, setErrors] = useState<PurchaseOrderFormErrorMap>({})
  const [saveError, setSaveError] = useState<string | null>(null)
  const [dirty, setDirty] = useState(false)

  useEffect(() => {
    onDirtyChange?.(dirty)
  }, [dirty, onDirtyChange])

  const handleFieldChange = useCallback(<K extends keyof PurchaseOrderForm>(field: K, value: PurchaseOrderForm[K]) => {
    setDirty(true)
    setValues((current) =>
      produce(current, (draft) => {
        draft[field] = value as never
      })
    )
    setErrors((current) => clearPurchaseOrderFormErrors(current, [field]))
  }, [])

  const handleSubmit = useCallback(async () => {
    if (orderId < 1) return
    const validationErrors = validatePurchaseOrderForm(values)
    if (hasPurchaseOrderFormErrors(validationErrors)) {
      setErrors(validationErrors)
      return
    }
    setSaveError(null)

    const editId = existingId != null && existingId > 0 ? existingId : null

    try {
      const result =
        mode === 'edit' && editId != null
          ? await updatePurchaseOrder({
              id: editId,
              traderId,
              data: purchaseOrderFormToUpdateWire(values, ALL_DIRTY)
            })
          : await createPurchaseOrder({ traderId, data: purchaseOrderFormToCreateWire(values, orderId) })
      if (result.id == null) {
        throw new Error('Saving the purchase order did not return an id.')
      }
      setDirty(false)
      onComplete({ purchaseOrderId: result.id })
    } catch (err: unknown) {
      const message = isError(err) ? err.message : 'Failed to save the purchase order.'
      setSaveError(message)
      toast({ title: 'Save failed', description: message, variant: 'destructive' })
    }
  }, [createPurchaseOrder, updatePurchaseOrder, existingId, mode, onComplete, orderId, traderId, values])

  const stepCtx = useMemo<PurchaseOrderStepCtx>(
    () => ({
      vals: values,
      errors,
      handleFieldChange
    }),
    [values, errors, handleFieldChange]
  )

  const stepDefs = useMemo(() => bindWizardSteps(PURCHASE_ORDER_STEP_MODULES, stepCtx), [stepCtx])

  const flow = useReportedWizardFlow(stepDefs, {
    stepSelection: 'gated',
    onNavStateChange,
    onExitFirstStep: cancelOnFirstStep ? onCancel : undefined
  })

  return (
    <WizardFlowContent
      flow={flow}
      onSubmit={() => void handleSubmit()}
      cancelOnFirstStep={cancelOnFirstStep}
      disabled={isSaving}
      submitDisabled={isSaving || !detailsStep.isComplete?.(stepCtx)}
      submitIcon={<Check className="h-4 w-4 mr-1" />}
      submitLabel={isSaving ? 'Saving…' : mode === 'edit' ? 'Save & continue' : 'Create purchase order'}
      navClassName="flex justify-between gap-2"
    >
      {saveError ? <p className="text-sm text-destructive">{saveError}</p> : null}
    </WizardFlowContent>
  )
}
